import { Component, computed, input, signal, ChangeDetectionStrategy } from '@angular/core';

import { AnimatedImage } from '@spaces-ui/reduced-motion';

import {
  USE_NATIVE_EMOJI,
  getEmojiNativeById,
  getEmojiSpritePositionWithSkin,
  emojiSpriteClasses,
  parseEmojiId,
} from '../../models';

/** Custom emoji data for rendering custom (non-standard) emojis. */
export interface CustomEmojiRenderData {
  id: string;
  name: string;
  imageUrl: string;
  custom: true;
}

@Component({
  selector: 'mdw-emoji',
  templateUrl: './mdw-emoji-details.html',
  styleUrls: ['./mdw-emoji-details.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AnimatedImage],
})
export class MdwEmojiDetails {
  protected readonly isNative = USE_NATIVE_EMOJI;

  public emojiName = input<string>();
  public size = input<number>();
  /** Custom emoji data object — when provided, renders an <img> instead of native/sprite emoji. */
  public customEmojiData = input<CustomEmojiRenderData | undefined>();

  private _programmiticEmoji = signal<string | null>(null);

  /** Strips surrounding colons if present (e.g. `:grinning:` → `grinning`). */
  protected resolvedEmojiId = computed(() => {
    const raw = this.emojiName() ?? this._programmiticEmoji();

    if (!raw) {
      return null;
    }

    return raw.replace(/^:/, '').replace(/:$/, '');
  });

  /** Parses the resolved ID into base name and optional skin tone (e.g. `wave::skin-tone-3`). */
  private parsedEmoji = computed(() => {
    const id = this.resolvedEmojiId();

    if (!id) {
      return null;
    }

    return parseEmojiId(id);
  });

  protected isCustom = computed(() => !!this.customEmojiData());

  /** Resolved native character for the emoji, including skin tone variants. */
  protected nativeChar = computed(() => {
    const parsed = this.parsedEmoji();

    if (!parsed) {
      return null;
    }

    return getEmojiNativeById(parsed.baseId, parsed.skinTone);
  });

  /** Sprite CSS classes for non-native rendering, including skin tone variants. */
  protected spriteClass = computed(() => {
    const parsed = this.parsedEmoji();

    if (!parsed) {
      return '';
    }

    return emojiSpriteClasses(getEmojiSpritePositionWithSkin(parsed.baseId, parsed.skinTone));
  });

  public setEmoji(emojiName: string) {
    this._programmiticEmoji.set(emojiName);
  }
}
